import { Turbo } from "@hotwired/turbo-rails";

// Custom Turbo Stream actions. Register these once at boot before any stream
// arrives. Each handler runs with `this` bound to the <turbo-stream> element,
// so they must stay as regular functions (not arrows).
export function registerTurboStreamActions() {
  Turbo.StreamActions.redirect = function () {
    Turbo.visit(this.target, { frame: "_top" });
  };

  Turbo.StreamActions.dispatch_event = function () {
    const target = this.getAttribute("target");
    const eventName = this.getAttribute("event");
    const detail = JSON.parse(this.getAttribute("detail") || "{}");
    document
      .querySelector(target)
      ?.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail }));
  };

  Turbo.StreamActions.scroll_to = function () {
    const target = document.getElementById(this.target);
    if (target) {
      requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    }
  };

  Turbo.StreamActions.focus = function () {
    const target = document.getElementById(this.target);
    if (target) {
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
  };

Turbo.StreamActions.update_if_empty = function () {
    this.targetElements.forEach((target) => {
      if (target.children.length === 0) {
        target.innerHTML = "";
        target.append(this.templateContent.cloneNode(true));
      }
    });
  };

  Turbo.StreamActions.upsert_frame = function () {
    const target = document.getElementById(this.target);
    if (!target) return;

    const fragment = this.templateContent.cloneNode(true);
    const frame = fragment.firstElementChild;

    if (!frame?.id) {
      target.append(fragment);
      return;
    }

    if (document.getElementById(frame.id)) {
      return;
    }

    target.append(fragment);
  };

  // ── Chat SPA bridge ────────────────────────────────────────────────
  // The chat is not a Turbo view: it owns its own DOM and syncs with the
  // homeserver directly, so Rails cannot morph it. These actions are the whole
  // channel Rails has into it — a JSON payload in the stream template, re-emitted
  // as the DOM event the SPA already listens for. They replaced a hidden marker
  // <div> the SPA watched with a MutationObserver, and the inline <script> tags
  // that used to close the dialog.
  Turbo.StreamActions.open_chat_room = function () {
    dispatchChatEvent(this, "bryght-chat:open-room");
  };

  // A 1:1 chat that has no room yet: the SPA opens it as a draft and creates
  // the room with the first message.
  Turbo.StreamActions.open_chat_draft = function () {
    dispatchChatEvent(this, "bryght-chat:open-draft");
  };

  Turbo.StreamActions.chat_room_updated = function () {
    dispatchChatEvent(this, "bryght-chat:room-updated");
  };

  Turbo.StreamActions.close_dialog = function () {
    document.querySelector("dialog[open]")?.close();
  };

  Turbo.StreamActions.reply_added = function () {
    const target = this.getAttribute("target");
    const container = document.getElementById(target);
    if (!container) return;

    const moreRepliesId = target.replace(/_replies$/, "_more_replies");
    const existingMoreReplies = container.querySelector(`#${moreRepliesId}`);
    const isCollapsed = !!existingMoreReplies && !existingMoreReplies.querySelector("turbo-frame");

    if (isCollapsed) {
      const updatedMoreReplies = this.templateContent.querySelector(`#${moreRepliesId}`);
      if (updatedMoreReplies) existingMoreReplies.replaceWith(updatedMoreReplies);
    } else {
      const newReply = this.templateContent.querySelector(`turbo-frame:not(#${moreRepliesId})`);
      if (newReply) container.append(newReply);
    }
  };
}

function dispatchChatEvent(streamElement, eventName) {
  let detail;

  try {
    detail = JSON.parse(streamElement.templateContent.textContent);
  } catch (error) {
    console.warn(`[BryghtChat] malformed ${eventName} payload`, error);
    return;
  }

  window.dispatchEvent(new CustomEvent(eventName, { detail }));
}
